import { logged } from '@sembo-private/utils';
import type { Observable } from 'rxjs';
import {
  takeUntil,
  share,
  mergeMap,
  shareReplay,
  scan,
  distinctUntilChanged,
  from,
  mergeAll,
  toArray,
  map,
  filter,
  groupBy,
  distinct,
  merge,
  iif,
  take,
  concat,
  EMPTY,
  of,
} from 'rxjs';
import { isEqual, uniq } from 'lodash';

import {
  isHotelIngredientResponse,
  isFlightIngredientResponse,
  isFerryIngredientResponse,
  isAvailableHotelIngredientResponse,
  isOwnStayIngredientResponse,
} from '@libs/search-service-smart-search';
import {
  IngredientType,
  AvailabilityOrder,
} from '@libs/trip-planner-interfaces';
import type {
  HotelIngredientAdapter,
  HotelIngredientResponse,
  ItineraryWithPrice,
  OverviewStaticInfoResult,
  ItinerarySearchCriteria,
  ItineraryResponse,
  ItineraryMainIngredientResponse,
  HeartedHotel,
  ActivityIngredientAdapter,
  IngredientId,
  OwnStayIngredientAdapter,
} from '@libs/trip-planner-interfaces';
import { SemboDate } from '@libs/sembo-dates';
import { isDefined } from '@libs/utils';

import { ingredientDateComparer } from '../helpers/comparisons';
import type { SmartSearchService } from '../search/smartSearchService';
import {
  isHotelIngredientAdapter,
  isOwnStayIngredientAdapter,
} from '../recipe/ingredients/typeGuards';

type HotelCodeByIngredient = {
  hotelCode: string;
  ingredientId: IngredientId;
};

type HeartedHotelsByIngredient = Map<IngredientId, HeartedHotel[]>;
type ActivitiesByIngredient = Map<IngredientId, ActivityIngredientAdapter[]>;

export class ItinerarySearchResults {
  public readonly itineraries: Observable<ItineraryWithPrice>;
  public readonly cheapestItinerary: Observable<ItineraryWithPrice>;
  public readonly staticInfo: Observable<OverviewStaticInfoResult>;
  private uniqueHotelCodesByIngredient: HotelCodeByIngredient[] = [];

  constructor(
    searchService: SmartSearchService,
    criteria: ItinerarySearchCriteria,
    cancel: Observable<unknown>
  ) {
    const searchResults = searchService
      .searchItineraries(criteria)
      .pipe(takeUntil(cancel), share());

    this.itineraries = searchResults.pipe(
      mergeMap((itineraryResponse) => this.flattenItinerary(itineraryResponse)),
      logged('itineraries'),
      shareReplay()
    );

    this.cheapestItinerary = this.getCheapestItinerary(this.itineraries);

    this.staticInfo = this.getStaticInfo(criteria, searchService);
  }

  private flattenItinerary({
    date,
    itinerary,
    prices,
  }: ItineraryResponse): Observable<ItineraryWithPrice> {
    return from(itinerary).pipe(
      mergeAll(),
      toArray(),
      map<ItineraryMainIngredientResponse[], ItineraryWithPrice>(
        (ingredients) => {
          const itinerary = ingredients.sort(ingredientDateComparer);

          itinerary.map((ingredient) => {
            if (
              isHotelIngredientResponse(ingredient) ||
              isOwnStayIngredientResponse(ingredient)
            ) {
              ingredient.subIngredientResponse.sort(ingredientDateComparer);
            }

            return ingredient;
          });

          const ownStayOnlyItinerary = itinerary.filter(
            isOwnStayIngredientResponse
          );
          const hotelOnlyItinerary = itinerary.filter(
            isHotelIngredientResponse
          );
          const flightOnlyItinerary = itinerary.filter(
            isFlightIngredientResponse
          );
          const ferryOnlyItinerary = itinerary.filter(
            isFerryIngredientResponse
          );

          const key = hotelOnlyItinerary
            .map((hotelIngredient) =>
              isAvailableHotelIngredientResponse(hotelIngredient)
                ? hotelIngredient.suggested.addToBasketToken
                : hotelIngredient.ingredientId
            )
            .join(',');

          const isPartial = itinerary.some((response) => {
            if (
              isAvailableHotelIngredientResponse(response) ||
              isOwnStayIngredientResponse(response)
            ) {
              return response.subIngredientResponse.some(
                (subIngredient) => !subIngredient.isAvailable
              );
            }

            return !response.isAvailable;
          });

          const isOnlyOwnStay = itinerary.every(
            (response) =>
              isOwnStayIngredientResponse(response) &&
              !response.subIngredientResponse.length
          );

          return {
            key,
            date,
            totalPrice: isPartial || isOnlyOwnStay ? null : prices.totalPrice,
            totalPriceWithAlternative: prices.totalPriceWithAlternative,
            itinerary,
            ownStayOnlyItinerary,
            hotelOnlyItinerary,
            flightOnlyItinerary,
            ferryOnlyItinerary,
          };
        }
      ),
      filter(
        (itineraryWithPrice) =>
          itineraryWithPrice.itinerary.length === itinerary.length
      )
    );
  }

  private getCheapestItinerary(
    itineraries: Observable<ItineraryWithPrice>
  ): Observable<ItineraryWithPrice> {
    const cheapestCompleteItinerary$ = itineraries.pipe(
      filter((itinerary) => !!itinerary.totalPrice),
      scan((cheapestItinerary, currentItinerary) => {
        if (!currentItinerary.totalPrice) {
          return cheapestItinerary;
        }

        if (!cheapestItinerary.totalPrice) {
          return currentItinerary;
        }

        const isCurrentItineraryCheaper =
          currentItinerary.totalPrice.amount <
          cheapestItinerary.totalPrice.amount;

        const hasCurrentItinerarySamePrice =
          currentItinerary.totalPrice.amount ===
          cheapestItinerary.totalPrice.amount;

        const isCurrentItineraryEarlier = new SemboDate(
          currentItinerary.date
        ).isBefore(cheapestItinerary.date);

        if (
          isCurrentItineraryCheaper ||
          (hasCurrentItinerarySamePrice && isCurrentItineraryEarlier)
        ) {
          return currentItinerary;
        }

        return cheapestItinerary;
      }),
      distinctUntilChanged((previousItinerary, currentItinerary) => {
        const isSameAmount =
          previousItinerary.totalPrice?.amount ===
          currentItinerary.totalPrice?.amount;
        const isSameDate = new SemboDate(previousItinerary.date).isSame(
          currentItinerary.date
        );

        return isSameAmount && isSameDate;
      }),
      shareReplay()
    );

    const firstPartialItinerary$ = itineraries.pipe(
      filter((itinerary) => !itinerary.totalPrice),
      take(1),
      shareReplay()
    );

    return concat(
      cheapestCompleteItinerary$,
      firstPartialItinerary$.pipe(takeUntil(cheapestCompleteItinerary$))
    );
  }

  private getStaticInfo(
    criteria: ItinerarySearchCriteria,
    searchService: SmartSearchService
  ): Observable<OverviewStaticInfoResult> {
    const hotelOnlyIngredients =
      criteria.userCriteria.ingredients?.filter(isHotelIngredientAdapter) ?? [];
    const ownStayOnlyIngredients =
      criteria.userCriteria.ingredients?.filter(isOwnStayIngredientAdapter) ??
      [];

    const recipeProductCodesStaticInfoAdapters: HotelIngredientAdapter[] =
      this.getRecipeProductCodesStaticInfoAdapters(
        hotelOnlyIngredients,
        ownStayOnlyIngredients
      );

    const relevantItineraryStaticInfoAdapters: Observable<
      HotelIngredientAdapter[]
    > = this.getRelevantItineraryStaticInfoAdapters(
      hotelOnlyIngredients,
      criteria.selectedDate
    );

    const remainingItinerariesStaticInfoAdapters: Observable<
      HotelIngredientAdapter[]
    > = this.getRemainingItinerariesStaticInfoAdapters(hotelOnlyIngredients);

    const suggestedHotelStaticInfoAdapters: Observable<
      HotelIngredientAdapter[]
    > = this.getSuggestedHotelsStaticInfoAdapters(hotelOnlyIngredients);

    const staticInfoAdapters: Observable<HotelIngredientAdapter[]> = merge(
      of(recipeProductCodesStaticInfoAdapters),
      relevantItineraryStaticInfoAdapters,
      remainingItinerariesStaticInfoAdapters,
      suggestedHotelStaticInfoAdapters
    ).pipe(filter((adapters) => adapters.length > 0));

    return staticInfoAdapters.pipe(
      mergeMap((itineraryAdapters) =>
        searchService.searchOverviewStaticInfo(itineraryAdapters)
      ),
      share()
    );
  }

  private getRecipeProductCodesStaticInfoAdapters(
    hotelOnlyIngredients: HotelIngredientAdapter[],
    ownStayOnlyIngredients: OwnStayIngredientAdapter[]
  ): HotelIngredientAdapter[] {
    const heartedHotelsByIngredient: HeartedHotelsByIngredient =
      getHeartedHotelCodesByIngredient(hotelOnlyIngredients);

    const activitiesByIngredient: ActivitiesByIngredient =
      getActivityCodesByIngredient([
        ...hotelOnlyIngredients,
        ...ownStayOnlyIngredients,
      ]);

    const destinationProductsIngredientIds = uniq([
      ...heartedHotelsByIngredient.keys(),
      ...activitiesByIngredient.keys(),
    ]);

    if (destinationProductsIngredientIds.length === 0) {
      return [];
    }

    return destinationProductsIngredientIds.map((ingredientId) => ({
      constraint: {
        boardBasis: [],
        selectedHotelTypes: [],
      },
      heartedHotels: heartedHotelsByIngredient.get(ingredientId) ?? [],
      subIngredients: activitiesByIngredient.get(ingredientId) ?? [],
      ingredientId,
      ingredientType: IngredientType.Hotel,
      suggestedBy: AvailabilityOrder.None,
    }));

    function getHeartedHotelCodesByIngredient(
      hotelOnlyIngredients: HotelIngredientAdapter[]
    ): HeartedHotelsByIngredient {
      return hotelOnlyIngredients.reduce(
        (heartedHotelsByIngredient, hotelIngredient) => {
          if (hotelIngredient.heartedHotels.length > 0) {
            heartedHotelsByIngredient.set(
              hotelIngredient.ingredientId,
              hotelIngredient.heartedHotels
            );
          }
          return heartedHotelsByIngredient;
        },
        new Map<IngredientId, HeartedHotel[]>()
      );
    }

    function getActivityCodesByIngredient(
      stayOnlyIngredients: (HotelIngredientAdapter | OwnStayIngredientAdapter)[]
    ): ActivitiesByIngredient {
      return stayOnlyIngredients.reduce(
        (activitiesByIngredient, stayIngredient) => {
          if (stayIngredient.subIngredients.length > 0) {
            activitiesByIngredient.set(
              stayIngredient.ingredientId,
              stayIngredient.subIngredients
            );
          }

          return activitiesByIngredient;
        },
        new Map<IngredientId, ActivityIngredientAdapter[]>()
      );
    }
  }

  private getRelevantItineraryStaticInfoAdapters(
    hotelOnlyIngredients: HotelIngredientAdapter[],
    selectedDate?: Date
  ): Observable<HotelIngredientAdapter[]> {
    const selectedItinerary = this.itineraries.pipe(
      filter(
        (itinerary) =>
          itinerary.date.toISOString() === selectedDate?.toISOString()
      )
    );

    const relevantItinerary = iif(
      () => isDefined(selectedDate),
      selectedItinerary,
      this.cheapestItinerary
    );

    const relevantItineraryCodes: Observable<HotelCodeByIngredient[]> =
      relevantItinerary.pipe(
        map((itinerary) =>
          this.getNewItineraryHotelCodes(itinerary.hotelOnlyItinerary)
        )
      );

    return relevantItineraryCodes.pipe(
      map((itineraryHotelCodes) => {
        const availableHotelIngredientAdapters =
          itineraryHotelCodes.map<HotelIngredientAdapter>(
            (itineraryHotelCode) =>
              this.createHotelIngredientAdapter(
                hotelOnlyIngredients,
                [itineraryHotelCode.hotelCode],
                itineraryHotelCode.ingredientId
              )
          );

        return availableHotelIngredientAdapters;
      })
    );
  }

  private getRemainingItinerariesStaticInfoAdapters(
    hotelOnlyIngredients: HotelIngredientAdapter[]
  ): Observable<HotelIngredientAdapter[]> {
    const remainingItinerariesCodes: Observable<HotelCodeByIngredient[]> =
      this.itineraries.pipe(
        map((itinerary) =>
          this.getNewItineraryHotelCodes(itinerary.hotelOnlyItinerary)
        )
      );

    return remainingItinerariesCodes.pipe(
      mergeMap((itineraryHotelCodesByIngredient) =>
        from(itineraryHotelCodesByIngredient)
      ),
      groupBy((staticInfo) => staticInfo.ingredientId),
      mergeMap((staticInfoGroup) => {
        const itinerariesHotelCodes = staticInfoGroup.pipe(
          map((staticInfo) => staticInfo.hotelCode),
          distinct(),
          toArray()
        );
        const availableHotelIngredientAdapters: Observable<HotelIngredientAdapter> =
          itinerariesHotelCodes.pipe(
            mergeMap((hotelCodes) => {
              if (hotelCodes.length === 0) {
                return EMPTY;
              }

              return of(
                this.createHotelIngredientAdapter(
                  hotelOnlyIngredients,
                  hotelCodes,
                  staticInfoGroup.key
                )
              );
            })
          );

        return availableHotelIngredientAdapters;
      }),
      toArray(),
      share()
    );
  }

  private getSuggestedHotelsStaticInfoAdapters(
    hotelOnlyIngredients: HotelIngredientAdapter[]
  ): Observable<HotelIngredientAdapter[]> {
    const suggestedHotelCodes: Observable<HotelCodeByIngredient[]> =
      this.itineraries.pipe(
        map((itinerary) =>
          this.getSuggestionsHotelCodes(itinerary.hotelOnlyItinerary)
        )
      );

    return suggestedHotelCodes.pipe(
      mergeMap((suggestedHotelCodesByIngredient) =>
        from(suggestedHotelCodesByIngredient)
      ),
      groupBy((staticInfo) => staticInfo.ingredientId),
      mergeMap((staticInfoGroup) => {
        const itinerariesHotelCodes = staticInfoGroup.pipe(
          map((staticInfo) => staticInfo.hotelCode),
          distinct(),
          toArray()
        );

        return itinerariesHotelCodes.pipe(
          map((hotelCodes) =>
            this.createHotelIngredientAdapter(
              hotelOnlyIngredients,
              hotelCodes,
              staticInfoGroup.key
            )
          )
        );
      }),
      toArray(),
      share()
    );
  }

  private getNewItineraryHotelCodes(
    hotelItinerary: HotelIngredientResponse[]
  ): HotelCodeByIngredient[] {
    return hotelItinerary.reduce(
      (codesByIngredient: HotelCodeByIngredient[], hotel) => {
        if (isAvailableHotelIngredientResponse(hotel)) {
          const suggestedHotelCodeByIngredient: HotelCodeByIngredient = {
            hotelCode: hotel.suggested.code,
            ingredientId: hotel.ingredientId,
          };

          this.pushOnlyNewCodesByIngredient(
            suggestedHotelCodeByIngredient,
            codesByIngredient,
            this.uniqueHotelCodesByIngredient
          );
        }

        if (hotel.alternativeSuggestion) {
          const alternativeSuggestedHotelCodeByIngredient: HotelCodeByIngredient =
            {
              hotelCode: hotel.alternativeSuggestion.code,
              ingredientId: hotel.ingredientId,
            };

          this.pushOnlyNewCodesByIngredient(
            alternativeSuggestedHotelCodeByIngredient,
            codesByIngredient,
            this.uniqueHotelCodesByIngredient
          );
        }

        return codesByIngredient;
      },
      []
    );
  }

  private getSuggestionsHotelCodes(
    hotelItinerary: HotelIngredientResponse[]
  ): HotelCodeByIngredient[] {
    return hotelItinerary.reduce(
      (codesByIngredient: HotelCodeByIngredient[], hotel) => {
        if (isAvailableHotelIngredientResponse(hotel)) {
          hotel.suggestions.forEach((suggestion) => {
            const suggestedHotelCodeByIngredient: HotelCodeByIngredient = {
              hotelCode: suggestion.code,
              ingredientId: hotel.ingredientId,
            };

            this.pushOnlyNewCodesByIngredient(
              suggestedHotelCodeByIngredient,
              codesByIngredient,
              this.uniqueHotelCodesByIngredient
            );
          });
        }

        return codesByIngredient;
      },
      []
    );
  }

  private pushOnlyNewCodesByIngredient(
    codeByIngredient: HotelCodeByIngredient,
    existingCodesByIngredient: HotelCodeByIngredient[],
    uniqueCodesByIngredient: HotelCodeByIngredient[]
  ): void {
    const isNewSuggestedHotelCodeByIngredient = !uniqueCodesByIngredient.find(
      (uniqueSuggestedHotelCodeByIngredient) =>
        isEqual(uniqueSuggestedHotelCodeByIngredient, codeByIngredient)
    );

    if (isNewSuggestedHotelCodeByIngredient) {
      existingCodesByIngredient.push(codeByIngredient);
      uniqueCodesByIngredient.push(codeByIngredient);
    }
  }

  private createHotelIngredientAdapter(
    hotelOnlyIngredients: HotelIngredientAdapter[],
    hotelCodes: string[],
    ingredientId: IngredientId
  ): HotelIngredientAdapter {
    const currentHotelIngredient = hotelOnlyIngredients.find(
      (hotel) => hotel.ingredientId === ingredientId
    );

    if (!currentHotelIngredient) {
      throw new Error('HotelIngredient criteria not found for specified');
    }

    return {
      ...currentHotelIngredient,
      constraint: {
        boardBasis: [],
        selectedHotelTypes: [],
        hotelCodes,
        maxNumberOfHotels: hotelCodes.length,
      },
      subIngredients: [],
      heartedHotels: [],
    };
  }
}
